/**
 * Thinking Timeline
 *
 * Live visualization of the orchestrator's PDCA progress while a message is
 * being processed: what SAIA is planning, which tool it is running, what it
 * observed, and what it decided. Fed by the real `thinking` SSE events —
 * no guessing from answer text.
 */

import React from 'react';
import { Box, Paper, Typography, CircularProgress, keyframes } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import type { ThinkingEvent } from '../../../hooks/ai-chat-store';
import { buildWorkSteps, toolLabel, type WorkStep } from './work-steps';

interface ThinkingTimelineProps {
  steps: ThinkingEvent[];
  /** Whether the stream is still running (latest step pulses). */
  isActive: boolean;
  /** Text of the answer is already streaming — last step is "Antwort formulieren". */
  answering?: boolean;
  /** Max steps rendered; older ones collapse into a counter. */
  maxVisible?: number;
}

const STATE_ICON: Record<WorkStep['state'], React.ReactNode> = {
  running: <CircularProgress size={12} thickness={6} />,
  done: <CheckCircleIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
};

const ThinkingTimeline: React.FC<ThinkingTimelineProps> = ({
  steps: events,
  isActive,
  answering = false,
  maxVisible = 6,
}) => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();

  // Sekundenzähler: Antworten dauern mit Sol oft 10–20 s — sichtbar machen, dass es läuft.
  const startedAt = React.useRef(Date.now());
  const [seconds, setSeconds] = React.useState(0);
  React.useEffect(() => {
    if (!isActive) return undefined;
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [isActive]);

  const all = buildWorkSteps(events, answering, (name) => toolLabel(name, i18n.language), {
    understanding: t('workStepUnderstanding', 'Nachricht verstehen'),
    answering: t('workStepAnswering', 'Antwort formulieren'),
  });
  const hiddenCount = Math.max(0, all.length - maxVisible);
  const visible = all.slice(-maxVisible);

  return (
    <Paper className={classes.container} elevation={0}>
      <Box className={classes.header}>
        <PsychologyIcon fontSize="small" color="primary" />
        <Typography variant="caption" className={classes.headerText}>
          {isActive ? t('saiaWorking', 'SAIA arbeitet daran...') : t('saiaWorkedSteps', 'Erledigte Schritte')}
        </Typography>
        {isActive && seconds > 0 && (
          <Typography variant="caption" color="textSecondary">
            {t('workStepSeconds', '{{count}} s', { count: seconds })}
          </Typography>
        )}
      </Box>
      <Box className={classes.steps}>
        {hiddenCount > 0 && (
          <Typography variant="caption" color="textSecondary" className={classes.more}>
            {t('workStepsEarlier', '+ {{count}} frühere Schritte', { count: hiddenCount })}
          </Typography>
        )}
        {visible.map((step) => (
          <Box key={step.key} className={cx(classes.step, step.state === 'running' && isActive && classes.stepActive)}>
            <Box className={cx(classes.stepIcon, step.state === 'error' && 'error', step.state === 'done' && 'done')}>
              {STATE_ICON[step.state]}
            </Box>
            <Typography
              variant="caption"
              className={cx(classes.stepText, step.state === 'running' && classes.stepTextActive)}
              noWrap
            >
              {step.state === 'error' ? `${step.label} – ${t('workStepFailed', 'fehlgeschlagen, ich korrigiere')}` : step.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.45; }
  100% { opacity: 1; }
`;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    marginBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  headerText: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    flex: 1,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 22,
    opacity: 0.75,
  },
  stepActive: {
    opacity: 1,
    animation: `${pulse} 1.6s ease-in-out infinite`,
  },
  stepError: {
    opacity: 1,
  },
  stepIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    fontSize: '1rem',
    color: theme.palette.primary.main,
    '&.done': {
      color: theme.palette.success.main,
    },
    '&.error': {
      color: theme.palette.error.main,
    },
  },
  more: {
    paddingLeft: theme.spacing(3),
  },
  stepText: {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  stepTextActive: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

export default ThinkingTimeline;
